import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { styled } from '@mui/system';

// MUI
import { Grid, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import {
  InputLabel,
  Dropdown,
  OpenWorkflowText,
  BoldText,
  BlueText,
  StyledOpenInNewIcon,
} from './StyledComponents';

const CentredBlueText = styled(BlueText)`
  display: flex;
  align-items: center;
`;

const BlackUndoIcon = styled(UndoIcon)`
  margin-right: 8px;
  color: var(--color-text-primary);
`;

const DropdownLink = styled(Link)`
  text-decoration: none;
  margin-top: 8px;
  display: block;
`;

const DropdownContainer = styled('div')`
  max-width: 100%;
  overflow: hidden;
`;

function Dropdowns({
  updateState,
  handleSaveComponent,
  projects,
  components,
  getComponents,
  targetProjectId,
  targetComponentId,
  refreshProjects,
  refreshComponents,
  projectsRefreshing,
  componentsRefreshing,
  loopMode,
}) {
  const [projectRefreshSuccess, setProjectRefreshSuccess] = useState(false);
  const [componentRefreshSuccess, setComponentRefreshSuccess] = useState(false);

  useEffect(() => {
    let timer;
    if (projectRefreshSuccess) {
      timer = setTimeout(() => {
        setProjectRefreshSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [projectRefreshSuccess]);

  useEffect(() => {
    let timer;
    if (componentRefreshSuccess) {
      timer = setTimeout(() => {
        setComponentRefreshSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [componentRefreshSuccess]);

  const handleProjectRefresh = async () => {
    if (refreshProjects) {
      const result = await refreshProjects();
      if (result) {
        setProjectRefreshSuccess(true);
      }
    }
  };

  const handleComponentRefresh = async () => {
    if (refreshComponents) {
      const result = await refreshComponents();
      if (result !== false) {
        setComponentRefreshSuccess(true);
      }
    }
  };

  const getProjectLabel = projId => {
    let proj = projects.find(proj => proj.id === projId);
    return proj;
  };

  const getComponentLabel = compId => {
    if (!components) return '';
    let comp = components.find(comp => comp.id === compId);
    return comp;
  };

  // Handles updating the selected projectId
  // Also saves the component after setting the projectId
  const handleSelectProject = newProjectId => {
    updateState({
      targetProjectId: newProjectId,
      targetComponentId: '',
    }).then(() => {
      handleSaveComponent();
    });
  };

  // Handles updating the selected component id
  const handleSelectComponent = newComponentId => {
    updateState({ targetComponentId: newComponentId });
  };

  const renderProjectDropdown = () => {
    return (
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center">
          <InputLabel>
            Select the <BoldText>Target Workflow </BoldText> that this component will trigger
          </InputLabel>
          <Tooltip title={projectsRefreshing ? 'Refreshing projects...' : 'Refresh projects'}>
            <span>
              <IconButton
                size="small"
                onClick={handleProjectRefresh}
                aria-label="refresh projects"
                disabled={projectsRefreshing}
                color={projectRefreshSuccess ? 'success' : 'default'}
              >
                {projectsRefreshing ? (
                  <CircularProgress size={20} />
                ) : projectRefreshSuccess ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        <DropdownContainer>
          <Dropdown
            value={getProjectLabel(targetProjectId) ?? null}
            onChange={(e, newValue) => {
              if (newValue != null) handleSelectProject(newValue.id);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.label ? option.label : option.id}
              </li>
            )}
            size={'small'}
            options={projects}
            renderInput={params => <TextField {...params} placeholder={'Select project'} />}
          />
        </DropdownContainer>
      </Grid>
    );
  };

  const renderComponentsDropdown = () => {
    return (
      <>
        {!loopMode && (
          <Grid item xs={12}>
            <DropdownLink to={`/project/canvas/${targetProjectId}`} target="_blank">
              <OpenWorkflowText>
                <CentredBlueText>
                  <StyledOpenInNewIcon />
                  Open {getProjectLabel(targetProjectId)?.label ?? targetProjectId ?? 'workflow'} in a new tab
                </CentredBlueText>
              </OpenWorkflowText>
            </DropdownLink>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <InputLabel>
              {loopMode ? (
                <span className="alignItemCenter">
                  <BlackUndoIcon fontSize="small" />
                  Loop workflow back to...
                </span>
              ) : (
                <>
                  Select the <BoldText>Trigger from a Workflow </BoldText>
                  component in the <BoldText>Target Workflow</BoldText>
                </>
              )}
            </InputLabel>
            <Tooltip
              title={componentsRefreshing ? 'Refreshing components...' : 'Refresh components'}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleComponentRefresh}
                  aria-label="refresh components"
                  disabled={componentsRefreshing}
                  color={componentRefreshSuccess ? 'success' : 'default'}
                >
                  {componentsRefreshing ? (
                    <CircularProgress size={20} />
                  ) : componentRefreshSuccess ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <DropdownContainer>
            <Dropdown
              value={getComponentLabel(targetComponentId) ?? null}
              onChange={(e, newValue) => {
                if (newValue != null) handleSelectComponent(newValue.id);
              }}
              onFocus={() => {
                if (targetComponentId === '') {
                  getComponents(targetProjectId);
                }
              }}
              size={'small'}
              options={components ? components : []}
              renderInput={params => <TextField {...params} placeholder={'Select component'} />}
            />
          </DropdownContainer>
          {targetComponentId && !loopMode && (
            <DropdownLink
              to={`/project/canvas/${targetProjectId}?view=${targetComponentId}`}
              target="_blank"
            >
              <CentredBlueText>
                <StyledOpenInNewIcon />
                Open component in a new tab
              </CentredBlueText>
            </DropdownLink>
          )}
        </Grid>
      </>
    );
  };

  return (
    <>
      {projects && !loopMode && renderProjectDropdown()}
      {(targetProjectId || loopMode) && renderComponentsDropdown()}
    </>
  );
}

Dropdowns.propTypes = {
  updateState: PropTypes.func,
  handleSaveComponent: PropTypes.func,
  projects: PropTypes.array,
  components: PropTypes.array,
  getComponents: PropTypes.func,
  targetProjectId: PropTypes.string,
  targetComponentId: PropTypes.string,
  refreshProjects: PropTypes.func,
  refreshComponents: PropTypes.func,
  projectsRefreshing: PropTypes.bool,
  componentsRefreshing: PropTypes.bool,
  loopMode: PropTypes.bool,
};

export default Dropdowns;
